import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import RedirectBackButton from "components/RedirectBackButton";
import useGlobalContext from "hooks/globalData";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import {
  ListRequestOfToolLogFilter,
  ToolLogDto,
} from "services/KnowfullyAdminClient";
import { convertToZonedDateTime, convertToZonedTime } from "utils/dateUtils";

const TERMINAL_STATUSES = ["Completed", "Failed", "Cancelled"];
const POLL_INTERVAL_MS = 2000;
const INPUT_MAX_CHARS = 500;
const MAX_DISPLAYED_LOGS = 500;

const styles = {
  terminal: {
    backgroundColor: "#1e1e1e",
    color: "#00ff00",
    fontFamily: "monospace",
    fontSize: "0.85rem",
    p: 2,
    height: 400,
    overflowY: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
};

const statusColor = (
  status?: string,
): "default" | "warning" | "success" | "error" | "info" => {
  switch (status) {
    case "Running":
      return "warning";
    case "Completed":
      return "success";
    case "Failed":
      return "error";
    case "Enqueued":
      return "info";
    default:
      return "default";
  }
};

const DataToolRunPage = () => {
  const { runId } = useParams<{ runId: string }>();
  const { formatMessage: t } = useIntl();
  const { dataToolsService, dataToolsUrl } = useGlobalContext();

  const [logs, setLogs] = useState<ToolLogDto[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const lastLogIdRef = useRef(0);
  const [isTerminal, setIsTerminal] = useState(false);
  const logBoxRef = useRef<HTMLDivElement>(null);

  const runQuery = useQuery(
    ["dataToolsService.getRun", runId],
    async () => await dataToolsService.getRun(runId!),
    { refetchInterval: isTerminal ? false : POLL_INTERVAL_MS },
  );

  const run = runQuery.data?.data;

  useEffect(() => {
    if (run?.status && TERMINAL_STATUSES.includes(run.status)) {
      setIsTerminal(true);
    }
  }, [run?.status]);

  useEffect(() => {
    let cancelled = false;

    const fetchLogs = async () => {
      const after =
        lastLogIdRef.current === 0 ? undefined : lastLogIdRef.current;
      const response = await dataToolsService.getLogs(runId!, {
        pageSize: MAX_DISPLAYED_LOGS,
        parameters: { runId, after },
      } as ListRequestOfToolLogFilter);
      if (cancelled) {
        return;
      }
      setTotalLogs(response.totalRecords ?? 0);
      const newLogs = response.data ?? [];
      if (newLogs.length > 0) {
        setLogs((prev) =>
          [...prev, ...newLogs].slice(-MAX_DISPLAYED_LOGS),
        );
        lastLogIdRef.current =
          newLogs[newLogs.length - 1].id ?? lastLogIdRef.current;
      }
    };

    fetchLogs();
    if (isTerminal)
      return () => {
        cancelled = true;
      };

    const intervalId = setInterval(fetchLogs, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [runId, isTerminal, dataToolsService]);

  useEffect(() => {
    if (logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [logs]);

  const handleCancel = async () => {
    await dataToolsService.cancelRun(runId!);
    runQuery.refetch();
  };

  const handleDownload = async () => {
    const file = await dataToolsService.downloadLogs(runId!);
    const url = URL.createObjectURL(file.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${run?.toolKey ?? "tool"}-log.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const canCancel = run?.status === "Enqueued" || run?.status === "Running";

  const inputDisplay = run?.input
    ? run.input.length > INPUT_MAX_CHARS
      ? run.input.substring(0, INPUT_MAX_CHARS) + "..."
      : run.input
    : null;

  const rows = run
    ? [
        {
          title: t({ id: "started" }),
          component: <span>{convertToZonedDateTime(run.createdOn)}</span>,
        },
        {
          title: t({ id: "by" }),
          component: <span>{run.createdBy}</span>,
        },
        run.completedOn
          ? {
              title: t({ id: "completed" }),
              component: <span>{convertToZonedDateTime(run.completedOn)}</span>,
            }
          : null,
        inputDisplay
          ? {
              title: t({ id: "inputOptional" }),
              component: (
                <Box
                  component="span"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {inputDisplay}
                </Box>
              ),
            }
          : null,
      ].filter(Boolean)
    : [];

  return (
    <Container>
      <Box mb={2}>
        <RedirectBackButton
          redirectRoute={dataToolsUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>

      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Typography variant="h5">{run?.toolKey ?? "..."}</Typography>
        {run?.status && (
          <Chip label={run.status} color={statusColor(run.status)} />
        )}
        {canCancel && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleCancel}
          >
            {t({ id: "cancelButton" })}
          </Button>
        )}
      </Box>

      {rows.length > 0 && (
        <Box mb={3}>
          <TableContainer>
            <Table>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={row!.title}>
                    <TableCell component="th" scope="row">
                      <b>{row!.title}</b>
                    </TableCell>
                    <TableCell>{row!.component}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="subtitle2">{t({ id: "logs" })}</Typography>
          {logs.length >= MAX_DISPLAYED_LOGS && (
            <Typography variant="caption" color="text.secondary">
              {t(
                { id: "showingLastLogsHint" },
                { count: MAX_DISPLAYED_LOGS, total: totalLogs },
              )}
            </Typography>
          )}
        </Box>
        {totalLogs > 0 && (
          <Button variant="outlined" size="small" onClick={handleDownload}>
            {t({ id: "downloadFullLogButton" })}
          </Button>
        )}
      </Box>
      <Paper
        ref={logBoxRef as React.RefObject<HTMLDivElement>}
        component="div"
        sx={styles.terminal}
      >
        {logs.length === 0 && !isTerminal && (
          <span style={{ color: "#888" }}>{t({ id: "waitingForOutput" })}</span>
        )}
        {logs.map((log) => (
          <div key={log.id}>
            <span style={{ color: "#666" }}>
              [
              {log.createdOn
                ? convertToZonedTime(new Date(log.createdOn))
                : ""}
              ]{" "}
            </span>
            {log.message}
          </div>
        ))}
        {isTerminal && logs.length === 0 && (
          <span style={{ color: "#888" }}>{t({ id: "noOutput" })}</span>
        )}
      </Paper>
    </Container>
  );
};

export default DataToolRunPage;
